import type { SnippetLang } from '@gonfalon/code-block/languages';
import { msg } from '@gonfalon/messages/msg';
import { getSdkInfoByKey, POPULAR_SDK_KEYS, type PopularSdkKey, type SdkKey } from '@gonfalon/sdk-info/sdkInfo';
import type { SdkInitKey, SdkInitKeys } from '@gonfalon/sdk-info/types';

export const getDisplayName = (sdk: SdkKey) => getSdkInfoByKey(sdk).name;

export const getIcon = (sdk: SdkKey) => {
  const Icon = getSdkInfoByKey(sdk).icon;
  return <Icon size="small" />;
};

export const isPopularSdk = (sdk: SdkKey): sdk is PopularSdkKey => {
  return POPULAR_SDK_KEYS.includes(sdk as PopularSdkKey);
};

export const getPrismLang = (sdk: SdkKey): SnippetLang => {
  return getSdkInfoByKey(sdk).prismLang;
};

export const fillSdkInitKeys = (snippet: string, sdkInitKeys: SdkInitKeys, memberId: string) =>
  snippet
    .replaceAll('YOUR_SDK_KEY', sdkInitKeys.serverSide)
    .replaceAll('YOUR_MOBILE_KEY', sdkInitKeys.mobile)
    .replaceAll('YOUR_CLIENT_SIDE_ID', sdkInitKeys.clientSide)
    .replaceAll('YOUR_MEMBER_ID', memberId);

export const getSdkInitSnippet = (sdk: SdkKey, sdkInitKeys: SdkInitKeys, memberId: string) => {
  const metadata = getSdkInfoByKey(sdk);
  return metadata.initSnippet && fillSdkInitKeys(metadata.initSnippet, sdkInitKeys, memberId);
};

export const ENV_VAR_NAMES: Record<SdkInitKey, string> = {
  serverSide: 'LAUNCHDARKLY_SDK_KEY',
  mobile: 'LAUNCHDARKLY_MOBILE_KEY',
  clientSide: 'LAUNCHDARKLY_CLIENT_SIDE_ID',
};

export const getSdkKeyFromEnvironment = (sdkKebabName: SdkKey, sdkInitKeys: SdkInitKeys) => {
  const { initKey } = getSdkInfoByKey(sdkKebabName);

  return sdkInitKeys[initKey[0]];
};

/**
 * Human-readable label for the credential an SDK uses (e.g. "SDK key", "Mobile key",
 * "Client-side ID"). Use in user-facing copy that references the credential by name —
 * the generic "SDK key" is misleading for client-side and mobile users.
 */
export const getSdkKeyTypeLabel = (sdkKebabName: SdkKey): string => {
  const { initKey } = getSdkInfoByKey(sdkKebabName);
  switch (initKey[0]) {
    case 'serverSide':
      return msg('sdkSetup.credentialName.sdkKey');
    case 'mobile':
      return msg('sdkSetup.credentialName.mobileKey');
    case 'clientSide':
      return msg('sdkSetup.credentialName.clientSideId');
    default:
      return msg('sdkSetup.credentialName.generic');
  }
};
